import { useEffect, useState } from 'react';
import { Trophy, Zap, X } from 'lucide-react';
import confetti from 'canvas-confetti';
import { cn } from '@/lib/utils';

interface FocusMilestonePopupProps {
  milestone: number; // 25, 50, 75
  xpAwarded: number;
  onClose: () => void;
}

const MILESTONE_MESSAGES = {
  25: "Quarter way there! 🎯",
  50: "Halfway done! 💪",
  75: "Almost there! 🚀",
};

export function FocusMilestonePopup({ milestone, xpAwarded, onClose }: FocusMilestonePopupProps) {
  const [show, setShow] = useState(true);

  useEffect(() => {
    // Trigger confetti - reduced intensity for less distraction
    confetti({
      particleCount: 30,
      spread: 40,
      origin: { y: 0.6 },
      colors: ['#FFE08A', '#6B8E23', '#8E3B5A'],
    });

    // Auto-close after 3 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300); // Wait for fade out
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center p-4",
        "transition-opacity duration-300",
        show ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
    >
      {/* Backdrop - click to dismiss */}
      <div 
        className="absolute inset-0 bg-background/40"
        onClick={handleClose}
      />
      
      <div
        className={cn(
          "relative bg-card border-2 border-primary rounded-2xl p-6 shadow-lg max-w-sm w-full",
          "transform transition-all duration-300",
          show ? "scale-100" : "scale-95"
        )}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>

        <div className="text-center space-y-4">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 animate-bounce">
            <Trophy className="w-8 h-8 text-primary" />
          </div>

          {/* Message */}
          <div>
            <h3 className="text-2xl font-bold font-heading text-foreground">
              {MILESTONE_MESSAGES[milestone as keyof typeof MILESTONE_MESSAGES]}
            </h3>
            <p className="text-muted-foreground mt-1">
              {milestone}% Complete
            </p>
          </div>

          {/* XP Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full">
            <Zap className="w-4 h-4 text-secondary" />
            <span className="text-sm font-semibold text-foreground">
              +{xpAwarded} XP
            </span>
          </div>

          {/* Progress indicator */}
          <div className="text-xs text-muted-foreground">
            Keep going! You're doing great! 📚
          </div>
        </div>
      </div>
    </div>
  );
}
